import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

function det(m) {
  const n = m.length;
  if (n === 1) return m[0][0];
  if (n === 2) return m[0][0]*m[1][1] - m[0][1]*m[1][0];
  let d = 0;
  for (let j = 0; j < n; j++) {
    const sub = m.slice(1).map(r => r.filter((_, k) => k !== j));
    d += (j % 2 === 0 ? 1 : -1) * m[0][j] * det(sub);
  }
  return d;
}
function inverse(m) {
  const n = m.length;
  const d = det(m);
  if (Math.abs(d) < 1e-12) return null;
  const cof = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++) {
      const sub = m.filter((_, r) => r !== i).map(r => r.filter((_, k) => k !== j));
      cof[j][i] = ((i + j) % 2 === 0 ? 1 : -1) * det(sub) / d;
    }
  return cof;
}
function mk(rows, cols, fill = 0) {
  return Array.from({ length: rows }, (_, i) => Array.from({ length: cols }, (_, j) => fill === 'id' ? (i === j ? 1 : 0) : 0));
}
function randMat(rows, cols, lo = -9, hi = 9) {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => Math.floor(Math.random() * (hi - lo + 1)) + lo));
}

export default function MatrixCalc() {
  const [rowsA, setRowsA] = useState(3);
  const [colsA, setColsA] = useState(3);
  const [rowsB, setRowsB] = useState(3);
  const [colsB, setColsB] = useState(3);
  const [A, setA] = useState(() => [[1,2,3],[0,1,4],[5,6,0]]);
  const [B, setB] = useState(() => [[1,0,0],[0,1,0],[0,0,1]]);
  const [op, setOp] = useState('add');
  const [scalar, setScalar] = useState(2);

  const resizeA = (r, c) => {
    setRowsA(r); setColsA(c);
    setA(prev => {
      const n = mk(r, c);
      for (let i = 0; i < Math.min(r, prev.length); i++)
        for (let j = 0; j < Math.min(c, prev[i]?.length || 0); j++) n[i][j] = prev[i][j];
      return n;
    });
  };
  const resizeB = (r, c) => {
    setRowsB(r); setColsB(c);
    setB(prev => {
      const n = mk(r, c);
      for (let i = 0; i < Math.min(r, prev.length); i++)
        for (let j = 0; j < Math.min(c, prev[i]?.length || 0); j++) n[i][j] = prev[i][j];
      return n;
    });
  };
  const randomizeA = () => setA(randMat(rowsA, colsA));
  const randomizeB = () => setB(randMat(rowsB, colsB));

  const setCell = (M, setM) => (i, j, v) => {
    const n = M.map(r => [...r]); n[i][j] = +v || 0; setM(n);
  };

  const result = useMemo(() => {
    try {
      if (op === 'add' && rowsA === rowsB && colsA === colsB)
        return A.map((r, i) => r.map((v, j) => v + B[i][j]));
      if (op === 'sub' && rowsA === rowsB && colsA === colsB)
        return A.map((r, i) => r.map((v, j) => v - B[i][j]));
      if (op === 'mul' && colsA === rowsB) {
        const R = Array.from({ length: rowsA }, () => Array(colsB).fill(0));
        for (let i = 0; i < rowsA; i++) for (let j = 0; j < colsB; j++) for (let k = 0; k < colsA; k++) R[i][j] += A[i][k] * B[k][j];
        return R;
      }
      if (op === 'transposeA') return A[0].map((_, i) => A.map(r => r[i]));
      if (op === 'transposeB') return B[0].map((_, i) => B.map(r => r[i]));
      if (op === 'scalarA') return A.map(r => r.map(v => v * scalar));
      if (op === 'detA' && rowsA === colsA) return det(A);
      if (op === 'detB' && rowsB === colsB) return det(B);
      if (op === 'invA' && rowsA === colsA) return inverse(A);
      if (op === 'invB' && rowsB === colsB) return inverse(B);
      if (op === 'traceA' && rowsA === colsA) return A.reduce((s, r, i) => s + r[i], 0);
      if (op === 'traceB' && rowsB === colsB) return B.reduce((s, r, i) => s + r[i], 0);
    } catch { return null; }
    return null;
  }, [A, B, op, rowsA, colsA, rowsB, colsB, scalar]);

  const M = ({ mat, onCell, label, color = 'blue' }) => (
    <div className="inline-block">
      {label && <div className={`font-semibold mb-1 text-${color}-700`}>{label}</div>}
      <div className="inline-block bg-slate-50 p-2 rounded-lg border border-slate-200">
        {mat.map((row, i) => (
          <div key={i} className="flex gap-1 mb-1 last:mb-0">
            {row.map((v, j) => onCell
              ? <input key={j} type="number" step="any" value={v} onChange={e => onCell(i, j, e.target.value)}
                  className="w-14 h-10 text-center border border-slate-200 rounded bg-white text-sm focus:border-blue-500 outline-none" />
              : <div key={j} className="w-16 h-10 flex items-center justify-center border border-slate-200 rounded bg-white font-mono text-sm">{Number(v).toFixed(3)}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const needsB = ['add', 'sub', 'mul'].includes(op);
  const isScalar = ['detA', 'detB', 'traceA', 'traceB'].includes(op);
  const SIZES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15];
  const clampSize = (v) => Math.max(1, Math.min(20, Math.floor(+v || 1)));

  return (
    <CalcShell look="cnet"
      title="Matrix Calculator"
      subtitle="Add, subtract, multiply, transpose, invert, and compute determinant/trace of any rectangular matrix up to 6×6."
      about={<>
        <p><b>Matrix</b> is a rectangular array of numbers arranged in rows and columns. This tool supports independent row/column sizes for A and B, plus a random-matrix button.</p>
      </>}
    >
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <label className="text-sm font-semibold">Matrix A size:</label>
        <select value={rowsA} onChange={e => resizeA(+e.target.value, colsA)} data-testid="mat-a-rows" className="px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">{SIZES.map(s=><option key={s}>{s}</option>)}</select>
        <span className="text-slate-500">×</span>
        <select value={colsA} onChange={e => resizeA(rowsA, +e.target.value)} data-testid="mat-a-cols" className="px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">{SIZES.map(s=><option key={s}>{s}</option>)}</select>
        <span className="text-xs text-slate-500 ml-2">Custom:</span>
        <input type="number" min="1" max="20" value={rowsA} onChange={e => resizeA(clampSize(e.target.value), colsA)} data-testid="mat-a-rows-custom" className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-sm" placeholder="rows" />
        <span className="text-slate-500">×</span>
        <input type="number" min="1" max="20" value={colsA} onChange={e => resizeA(rowsA, clampSize(e.target.value))} data-testid="mat-a-cols-custom" className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-sm" placeholder="cols" />
        <button onClick={randomizeA} data-testid="mat-a-random" className="ml-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700">Randomize A</button>
        {needsB && <>
          <label className="text-sm font-semibold ml-3">B size:</label>
          <select value={rowsB} onChange={e => resizeB(+e.target.value, colsB)} data-testid="mat-b-rows" className="px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">{SIZES.map(s=><option key={s}>{s}</option>)}</select>
          <span className="text-slate-500">×</span>
          <select value={colsB} onChange={e => resizeB(rowsB, +e.target.value)} data-testid="mat-b-cols" className="px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">{SIZES.map(s=><option key={s}>{s}</option>)}</select>
          <span className="text-xs text-slate-500 ml-2">Custom:</span>
          <input type="number" min="1" max="20" value={rowsB} onChange={e => resizeB(clampSize(e.target.value), colsB)} data-testid="mat-b-rows-custom" className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-sm" />
          <span className="text-slate-500">×</span>
          <input type="number" min="1" max="20" value={colsB} onChange={e => resizeB(rowsB, clampSize(e.target.value))} data-testid="mat-b-cols-custom" className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-sm" />
          <button onClick={randomizeB} data-testid="mat-b-random" className="ml-2 px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700">Randomize B</button>
        </>}
      </div>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <label className="text-sm font-semibold">Operation:</label>
        <select value={op} onChange={e => setOp(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">
          <optgroup label="Two matrices">
            <option value="add">A + B</option><option value="sub">A − B</option><option value="mul">A × B</option>
          </optgroup>
          <optgroup label="Single matrix">
            <option value="transposeA">Aᵀ</option><option value="transposeB">Bᵀ</option>
            <option value="scalarA">k · A</option>
            <option value="detA">det(A)</option><option value="detB">det(B)</option>
            <option value="invA">A⁻¹</option><option value="invB">B⁻¹</option>
            <option value="traceA">trace(A)</option><option value="traceB">trace(B)</option>
          </optgroup>
        </select>
        {op === 'scalarA' && <>
          <label className="text-sm font-semibold ml-2">k:</label>
          <input type="number" step="any" value={scalar} onChange={e => setScalar(+e.target.value || 0)} className="w-20 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50" />
        </>}
      </div>

      <div className="flex flex-wrap gap-6 items-start">
        <M mat={A} onCell={setCell(A, setA)} label={`Matrix A (${rowsA}×${colsA})`} color="blue" />
        {needsB && <M mat={B} onCell={setCell(B, setB)} label={`Matrix B (${rowsB}×${colsB})`} color="green" />}
      </div>

      <div className="mt-6">
        <div className="font-semibold mb-2 text-slate-800">Result</div>
        {result === null || result === undefined ? (
          <div className="text-red-600">Undefined — singular matrix or dimensions don't match for {op}.</div>
        ) : isScalar ? (
          <div className="result-panel"><div className="result-title">{op.startsWith('det') ? 'Determinant' : 'Trace'}</div><div className="result-value" data-testid="mat-scalar">{Number(result).toFixed(6)}</div></div>
        ) : (
          <M mat={result} />
        )}
      </div>

      <StepsPanel steps={[
        `<b>Add / subtract:</b> element-wise — requires matching dimensions`,
        `<b>Multiply:</b> (AB)ᵢⱼ = Σₖ Aᵢₖ · Bₖⱼ — requires cols(A) = rows(B)`,
        `<b>Transpose:</b> swap rows and columns`,
        `<b>Determinant / Inverse / Trace:</b> require a square matrix`,
      ]} />
    </CalcShell>
  );
}
